import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { contextNameKey } from '../../utils/theFreaks';
import useFreaksT from './useFreaksT';
import FreaksAvatar from './FreaksAvatar';
import FreaksBotBadge from './FreaksBotBadge';
import { contextIcon } from './contextIcon';
import { FREAKS_GOLD, freaksSx as s } from './freaksStyles';

const MEDALS = ['🥇', '🥈', '🥉'];
const BOARDS = ['season', 'today', 'all'];

const chipSx = (selected) => ({
  mr: 1, minHeight: 36, color: selected ? 'white' : 'rgba(255,255,255,0.8)',
  backgroundColor: selected ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.08)',
  '& .MuiChip-icon': { color: 'inherit' },
  '&:hover': { backgroundColor: selected ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.14)' },
});

// The common Top Freaks board, with each freak's activity; filter by board and by activity.
export default function FreaksTopBoard({ myContext }) {
  const { t } = useFreaksT();
  const navigate = useNavigate();
  const [board, setBoard] = useState('season');
  const [context, setContext] = useState(null);
  const [top, setTop] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setTop(null);
    try {
      setTop(await agent.TheFreaks.top(board, context));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [board, context]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box data-testid="freaks-top" sx={{ color: 'white' }}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={board}
        onChange={(event, value) => value && setBoard(value)}
        sx={{
          '& .MuiToggleButton-root': { minHeight: 44, color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.2)', textTransform: 'none' },
          '& .Mui-selected': { color: 'white !important', backgroundColor: 'rgba(54,201,162,0.3) !important' },
        }}
      >
        {BOARDS.map((value) => <ToggleButton key={value} value={value}>{t(`theFreaks.top.${value}`)}</ToggleButton>)}
      </ToggleButtonGroup>
      <Box sx={{ display: 'flex', overflowX: 'auto', my: 1.25 }}>
        <Chip label={t('theFreaks.top.allContexts')} onClick={() => setContext(null)} sx={chipSx(!context)}/>
        <Chip
          data-testid="freaks-top-my-context"
          icon={contextIcon(myContext)}
          label={t(contextNameKey(myContext))}
          onClick={() => setContext(myContext)}
          sx={chipSx(context === myContext)}
        />
      </Box>
      <Typography sx={{ ...s.small, ...s.muted }}>🏆 {t('theFreaks.top.prizeInfo')}</Typography>
      <Typography sx={{ ...s.small, ...s.muted, mb: 1 }}>🥇 {t('theFreaks.top.seasonPrize')}</Typography>

      {!top && !failed ? <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress sx={{ color: DEFAULT_THEME_COLOR }}/></Box> : null}
      {failed ? <Typography sx={s.empty}>{t('theFreaks.errors.generic')}</Typography> : null}
      {top && !top.players.length ? <Typography sx={s.empty}>{t('theFreaks.top.empty')}</Typography> : null}
      {top?.players.map((player) => (
        <ButtonBase
          key={player.username}
          data-testid={`freaks-top-${player.username}`}
          // bots have no profile
          disabled={player.isBot}
          onClick={() => navigate(`/@${player.username}`)}
          sx={{
            display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left', py: 1, px: 0.75, borderRadius: 2.5,
            minHeight: 52, color: 'white', backgroundColor: player.isMe ? 'rgba(54,201,162,0.13)' : 'transparent',
          }}
        >
          <Typography sx={{ width: 36, fontWeight: 800, textAlign: 'center', mr: 0.75, flexShrink: 0 }}>
            {MEDALS[player.rank - 1] ?? `#${player.rank}`}
          </Typography>
          <FreaksAvatar username={player.username} imageUrl={player.imageUrl} size={36}/>
          <Box sx={{ ...s.grow, ml: 1.25 }}>
            <Box sx={s.row}>
              <Typography noWrap sx={{ ...s.bold, flexShrink: 1 }}>{player.isBot ? player.username : `@${player.username}`}</Typography>
              {player.isBot ? <FreaksBotBadge/> : null}
            </Box>
            <Box sx={{ ...s.row, gap: 0.5 }}>
              {contextIcon(player.context, { fontSize: 14, color: '#888' })}
              <Typography noWrap sx={{ ...s.small, ...s.muted }}>
                {player.character?.name ?? t(contextNameKey(player.context))} · {t('theFreaks.level', { level: player.level })}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ ...s.bold, color: FREAKS_GOLD, ml: 1 }}>{player.score}</Typography>
        </ButtonBase>
      ))}
      {top ? (
        <Typography sx={{ ...s.bold, textAlign: 'center', mt: 1.5 }} data-testid="freaks-top-me">
          {top.me.rank ? t('theFreaks.top.myRank', { rank: top.me.rank, score: top.me.score }) : t('theFreaks.top.unranked')}
        </Typography>
      ) : null}
    </Box>
  );
}
